import { defineNuxtPlugin, mobileBearerToken, useLayoutMode } from '#imports'
import { graphqlClientInjectionKey } from '~/composables/graphql'
import { getRequestHost } from 'h3'
import { useRequestHeaders, useRouter } from '#app'
import { AwesomeGraphQLClient } from 'awesome-graphql-client'
import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { getNewTokenFromCapacitor } from '~/composables/mobile/capacitor-bridge'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function mobileFetch(url: string, options?: any) {
    const router = useRouter()

    // Perform original fetch
    let response = await fetch(url, options)

    // Intercept 401 Unauthorized
    if (response.status === 401) {
        // Pause execution and get new token from Capacitor
        let newToken = ''
        try {
            newToken = await getNewTokenFromCapacitor()
        } catch {
            router.replace('/')
            mobileBearerToken.value = null
            alert(
                'Sorry, the app is having trouble finding your login credentials'
            )
            throw new Error()
        }
        mobileBearerToken.value = newToken

        // Clone the original request options but swap in the new token
        const newOptions = { ...options }
        newOptions.headers = {
            ...newOptions.headers,
            Authorization: 'Bearer ' + newToken
        }

        // Retry the original fetch with the new token
        response = await fetch(url, newOptions)

        // If the retry still returns 401, something went wrong
        if (response.status === 401) {
            router.replace('/')
            mobileBearerToken.value = null
            alert(
                'Sorry, we are having difficulty refreshing your login credentials'
            )
            throw new Error()
        }
    }

    return response
}

export default defineNuxtPlugin(nuxt => {
    const { isMobileApp } = useLayoutMode()

    let client: AwesomeGraphQLClient<TypedDocumentNode>
    if (process.server && nuxt.ssrContext) {
        const headers = useRequestHeaders()
        const event = nuxt.ssrContext.event
        const endpoint =
            'http://' +
            getRequestHost(event).replace(/(:8080|:3000)/, ':8000') +
            '/graphql-public'
        client = new AwesomeGraphQLClient({
            endpoint,
            fetchOptions: { headers },
            formatQuery: (query: TypedDocumentNode) => print(query)
        })
    } else {
        client = new AwesomeGraphQLClient({
            endpoint: '/graphql-public',
            formatQuery: (query: TypedDocumentNode) => print(query),
            fetch: isMobileApp.value ? mobileFetch : undefined
        })
    }

    nuxt.vueApp.provide(graphqlClientInjectionKey, client)
})
